import React from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { PostcodeCTAInput } from "../components/PostcodeCTA/PostcodeCTA";
import {
	COUPLE_TIER_ONE,
	COUPLE_TIER_THREE,
	COUPLE_TIER_TWO,
	SINGLE_TIER_ONE,
	SINGLE_TIER_THREE,
	SINGLE_TIER_TWO,
} from "../constants/pricing";

import "../../scss/main.scss";

const careTiers = [
	{
		number: "01",
		label: "Tier one",
		title: "Standard live-in care",
		description:
			"Everyday support that keeps home life running smoothly, with practical help, personal care and companionship at the person's preferred pace.",
		singlePrice: SINGLE_TIER_ONE,
		couplePrice: COUPLE_TIER_ONE,
		includes: [
			"Household tasks and support around the home",
			"Medication support",
			"Personal hygiene, dressing and toileting assistance",
			"Companionship and emotional support",
			"Some night-time care, typically twice a night or less",
		],
		tone: "one",
	},
	{
		number: "02",
		label: "Tier two",
		title: "Specialist live-in care",
		description:
			"For people with more involved needs, including conditions such as dementia or Parkinson's and support with routines that need a little more coordination.",
		singlePrice: SINGLE_TIER_TWO,
		couplePrice: COUPLE_TIER_TWO,
		includes: [
			"Household tasks and medication support",
			"More extensive personal care, including stoma, PEG or catheter support",
			"Hoisting where one person can complete it safely",
			"Help with challenging behaviour, such as refusing medication",
			"Carer breaks planned around the care schedule",
		],
		tone: "two",
	},
	{
		number: "03",
		label: "Tier three",
		title: "Advanced live-in care",
		description:
			"For significant care needs throughout the day, where continuous and specialist assistance makes life at home safer and more manageable.",
		singlePrice: SINGLE_TIER_THREE,
		couplePrice: COUPLE_TIER_THREE,
		includes: [
			"Support with all household tasks",
			"Total support for personal needs, including stoma, PEG or catheter care",
			"Complex one-to-one care and condition-specific support",
			"Hoisting that requires more than one carer",
			"Careful planning so there is no gap in support during carer breaks",
		],
		tone: "three",
	},
];

const additionalOptions = [
	{
		title: "A carer who can drive",
		price: "£40-£80 weekly",
		copy:
			"For a carer with a driving licence, or one who brings a car with them.",
	},
	{
		title: "Spousal support",
		price: "£100 weekly",
		copy:
			"When the carer is also helping another resident with light domestic duties.",
	},
	{
		title: "Managed food budget",
		price: "£50 weekly",
		copy: "If you would like us to help manage the carer's daily food budget.",
	},
];

const guideCollections = [
	{
		eyebrow: "Funding and planning",
		title: "Paying for care",
		links: [
			[
				"What are my funding options?",
				"/blog/what-are-my-funding-options-for-care/",
			],
			[
				"What is the cost of live-in care?",
				"/blog/what-is-the-cost-of-live-in-care-2/",
			],
			[
				"State and local authority funding",
				"/blog/a-guide-to-state-funded-care/",
			],
		],
	},
	{
		eyebrow: "Helpful reading",
		title: "Starting the conversation",
		links: [
			[
				"When is care needed?",
				"/blog/when-is-care-needed-how-to-spot-the-signs-and-what-to-do-next/",
			],
			[
				"What can a live-in carer help with?",
				"/blog/what-can-a-live-in-carer-help-with-2/",
			],
			[
				"Live-in care vs. care home",
				"/blog/live-in-care-vs-care-home-which-option-is-right/",
			],
		],
	},
];

export default function Pricing() {
	const pageData = useStaticQuery(graphql`
		query PricingQuery {
			site {
				siteMetadata {
					featureToggles {
						enableBlogContent
					}
				}
			}
		}
	`);

	const {
		site: {
			siteMetadata: {
				featureToggles: { enableBlogContent },
			},
		},
	} = pageData;

	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Live-in Care Costs and Pricing | Edyn Care"
				description="Compare Edyn's three live-in care tiers for individuals and couples, understand what is included and see which factors affect weekly care costs."
				path="/pricing/"
			/>
			<Header />

			<main id="main-content" className="edyn-pricing">
				<section className="pricing-hero">
					<div className="site-shell pricing-hero__panel">
						<div className="pricing-hero__copy">
							<p className="eyebrow">Live-in care pricing</p>
							<h1>Care at home, clearly priced.</h1>
							<p className="pricing-hero__lead">
								Choose the level of support that fits today, with a care plan
								that can change as life at home changes.
							</p>
							<ul className="pricing-hero__reassurance">
								<li>Transparent weekly starting prices</li>
								<li>A personal assessment before care begins</li>
								<li>Pricing for one person or a couple</li>
							</ul>
							<div className="button-row">
								<a className="button button--cream" href="#pricing-packages">
									Explore care packages
								</a>
								<Link className="button button--text-light" to="/care-consultation/">
									Talk to a Care Advisor <span aria-hidden="true">&#8594;</span>
								</Link>
							</div>
						</div>
						<div className="pricing-hero__visual">
							<StaticImage
								placeholder="blurred"
								src="../../static/assets/marketing-site/images/pricing-header-img.png"
								alt="A care professional discussing support at home"
							/>
							<div className="pricing-hero__rate">
								<span>Live-in care from</span>
								<strong>{SINGLE_TIER_ONE}</strong>
								<small>per week for one person</small>
							</div>
						</div>
					</div>
				</section>

				<section id="pricing-packages" className="pricing-packages">
					<div className="site-shell">
						<div className="pricing-heading">
							<div>
								<p className="eyebrow">Three care packages</p>
								<h2>Choose the support that is right for home.</h2>
							</div>
							<p>
								Our Care Advisors and Care Managers help you understand what is
								needed now, without paying for care that is not needed. Your
								weekly rate is then confirmed after a personal assessment.
							</p>
						</div>

						<div className="pricing-packages__grid">
							{careTiers.map((tier) => (
								<article
									className={`pricing-package pricing-package--${tier.tone}`}
									key={tier.label}
								>
									<div className="pricing-package__head">
										<div className="pricing-package__meta">
											<span>{tier.number}</span>
											<p>{tier.label}</p>
										</div>
										<h3>{tier.title}</h3>
										<p>{tier.description}</p>
									</div>

									<div className="pricing-package__prices">
										<div>
											<span>One person</span>
											<strong>{tier.singlePrice}</strong>
											<small>per week</small>
										</div>
										<div>
											<span>Two people</span>
											<strong>{tier.couplePrice}</strong>
											<small>per week</small>
										</div>
									</div>

									<div className="pricing-package__includes">
										<p>Included in this package</p>
										<ul>
											{tier.includes.map((item) => (
												<li key={item}>{item}</li>
											))}
										</ul>
									</div>
								</article>
							))}
						</div>

						<div className="pricing-rate-note">
							<span aria-hidden="true">&#10003;</span>
							<p>
								<strong>Every arrangement is personal.</strong> The final weekly
								price reflects the care needed, including any night-time,
								moving-and-handling or additional cover requirements.
							</p>
						</div>
					</div>
				</section>

				<section className="pricing-contact">
					<div className="site-shell pricing-contact__panel">
						<div className="pricing-contact__copy">
							<p className="eyebrow eyebrow--light">A clear next step</p>
							<h2>Talk through the right level of support.</h2>
							<p>
								Tell us a little about what is changing at home. We can explain
								which package is likely to fit, what affects the price and what
								happens next.
							</p>
							<a className="pricing-contact__call" href="tel:02039709900">
								<span>Call a Care Advisor</span>
								020 3970 9900
							</a>
						</div>
						<div className="pricing-contact__form">
							<p>Check whether live-in care is available in your area.</p>
							<PostcodeCTAInput />
							<small>
								There is no obligation. We will use your postcode to guide the
								conversation.
							</small>
						</div>
					</div>
				</section>

				<section className="pricing-add-ons">
					<div className="site-shell pricing-add-ons__panel">
						<div className="pricing-add-ons__intro">
							<p className="eyebrow">Tailor the arrangement</p>
							<h2>Helpful additions, when they make a difference.</h2>
							<p>
								Alongside the care package, some families choose extra practical
								support. We will explain any relevant cost clearly before care
								starts.
							</p>
						</div>
						<ul className="pricing-add-ons__list">
							{additionalOptions.map((option) => (
								<li key={option.title}>
									<div>
										<h3>{option.title}</h3>
										<p>{option.copy}</p>
									</div>
									<strong>{option.price}</strong>
								</li>
							))}
						</ul>
					</div>
				</section>

				{enableBlogContent && (
					<section className="pricing-guides">
						<div className="site-shell">
							<div className="pricing-guides__heading">
								<p className="eyebrow">Keep exploring</p>
								<h2>Helpful guides for the decisions ahead.</h2>
							</div>
							<div className="pricing-guides__grid">
								{guideCollections.map((collection) => (
									<article className="pricing-guide" key={collection.title}>
										<p>{collection.eyebrow}</p>
										<h3>{collection.title}</h3>
										<ul>
											{collection.links.map(([title, to]) => (
												<li key={to}>
													<Link to={to}>
														{title} <span aria-hidden="true">&#8594;</span>
													</Link>
												</li>
											))}
										</ul>
									</article>
								))}
							</div>
						</div>
					</section>
				)}
			</main>
			<Footer />
		</div>
	);
}
